/** Talking to the server. Every call either returns JSON or throws one of two errors. */

/** The server answered, but with an error (wrong input, not signed in, ...). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** The request never got an answer (offline, timeout, server down). Safe to retry. */
export class NetworkError extends Error {}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; timeoutMs?: number } = {},
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), init.timeoutMs ?? 15_000);
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? (init.body === undefined ? "GET" : "POST"),
      headers: init.body === undefined ? {} : { "content-type": "application/json" },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      credentials: "same-origin",
      signal: controller.signal,
    });
  } catch {
    throw new NetworkError("No connection");
  } finally {
    clearTimeout(timer);
  }
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    if (res.status >= 500 || res.status === 0) throw new NetworkError("The server didn't answer");
  }
  if (!res.ok) {
    if (res.status >= 500)
      throw new NetworkError((json as { error?: string })?.error ?? "Server error");
    throw new ApiError(
      res.status,
      (json as { error?: string })?.error ?? `Request failed (${res.status})`,
    );
  }
  return json as T;
}
